import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  agentMailboxAddress,
  assistantEmailFromPreview,
  assistantNameViolation,
} from "@/lib/contacts-logic";
import { assistantDisplayName } from "@/hooks/useAssistantIdentity";
import type {
  AssistantIdentity,
  Contact,
  MailOwnerVerifyStartResult,
  SetupStatus,
} from "@/types/contacts";
import { EmailRulesEditor } from "@/components/contacts/EmailRulesEditor";

/** mm:ss for the code-expiry countdown. */
function formatMmSs(total: number): string {
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/** A gateway method the runtime does not register yet (the B1/B2 verbs ship on
 *  a roll). Mirrors `useAssistantIdentity`'s `methodPending` — an ABSENT method
 *  must degrade to the honest pending note, while a genuine failure of a
 *  PRESENT method must not silently hide the affordance. */
function methodNotFound(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported/i.test(msg);
}

/**
 * `EmailSetupBlocks` — the §3.9 three-block e-mail setup (assistant-hub S2),
 * shared by the hero's e-mail editor dialog AND the legacy `?connect=email`
 * entry (which retired its E0 shell for this).
 *
 * Email is a TWO-ENDPOINT channel like the IMs:
 *   ① the ASSISTANT's mailbox — auto-provisioned, NO pairing; only its
 *     address shape is (eventually) recomposable, via the D-13 formula. The
 *     recomposition verbs are the mailsystem plan's B1 — until they land the
 *     affordance shows the honest "em preparação" state (§2.5).
 *   ② the From display name — writes `identity.set {assistant:
 *     {emailDisplayName}}` as a read-modify-write echoing the full editable
 *     block (C3). The lexserver resync (mailsystem B3) carries it onto real
 *     outbound mail; the note under the field says so honestly.
 *   ③ the USER's OWN e-mail — pairs via SEND-TEST-EMAIL + 6-digit code
 *     (`mail.owner_verify_start/confirm`, mailsystem B2 `bb3ca197c` — `mail.*`,
 *     NOT `email.*`, which is not a registered prefix). BUILT: address → code
 *     → confirm, with the five rejection codes rendered distinctly, a 10-min
 *     expiry countdown and a 60s resend cooldown mirroring the server's. The
 *     `verified` stamp lands on the OWNER contact and is rendered, never
 *     fabricated (CT-INV-7 discipline).
 *
 * The rules editor rehosts at the bottom (§3.8: "rules editor rehosts into
 * the email drawer" — this dialog is that home).
 */
export function EmailSetupBlocks({
  assistant,
  owner,
  onChanged,
}: {
  assistant: AssistantIdentity | null;
  owner: Contact | null;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);

  // ① — the provisioned address (creds-store SoT via setup.status; the
  // identity record's `emails` is usually empty).
  const [setup, setSetup] = useState<SetupStatus | null>(null);
  useEffect(() => {
    if (!rpc || connectionState !== "connected") return;
    let cancelled = false;
    rpc
      .setupStatus()
      .then((s) => !cancelled && setSetup(s))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [rpc, connectionState]);
  const address = agentMailboxAddress(setup?.items, assistant?.emails);

  // ② — the From display name (the ONE live write in this dialog).
  const [displayName, setDisplayName] = useState(assistant?.emailDisplayName ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setDisplayName(assistant?.emailDisplayName ?? "");
  }, [assistant?.emailDisplayName]);

  async function saveFrom() {
    if (!rpc || !assistant || saving) return;
    const violation = assistantNameViolation(assistant.name, []);
    if (violation) {
      setError(violation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // READ-MODIFY-WRITE echoing the FULL editable block (C3).
      await rpc.identitySet({
        assistant: {
          name: assistant.name,
          theme: assistant.theme,
          emoji: assistant.emoji,
          avatar: assistant.avatar,
          emails: assistant.emails,
          emailDisplayName: displayName.trim() || undefined,
        },
      });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  // ── ③ EP2 owner-email pairing (mailsystem B2) ─────────────────────────────
  // Same support-probe grammar as ①: the verbs ship on a roll, so until they
  // exist this degrades to the honest pending note instead of a dead button.
  const [verifySupported, setVerifySupported] = useState<boolean | undefined>(undefined);
  const [personalEmail, setPersonalEmail] = useState("");
  const [pendingVerify, setPendingVerify] = useState<
    { address: string; expiresAt: string; attemptsRemaining?: number } | null
  >(null);
  const [code, setCode] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  /** When the server's 60s resend cooldown lifts (epoch ms). The server is the
   *  authority — this only mirrors it so the button can say WHY it is off. */
  const [resendReadyAt, setResendReadyAt] = useState(0);

  /** The owner's already-verified address, if any. `verified` is stamped only
   *  by `mail.owner_verify_confirm`; the SPA renders it and never sets it. */
  const ownerVerified = owner?.emails?.find((e) => e.verified) ?? null;

  // One shared 1s tick drives BOTH countdowns (code expiry + resend cooldown).
  useEffect(() => {
    if (!pendingVerify && resendReadyAt === 0) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [pendingVerify, resendReadyAt]);

  const secondsLeft = pendingVerify
    ? Math.max(0, Math.floor((Date.parse(pendingVerify.expiresAt) - nowMs) / 1000))
    : 0;
  const resendIn = Math.max(0, Math.ceil((resendReadyAt - nowMs) / 1000));

  // DIRECT probe via `mail.owner_verify_status` (mailsystem `48066d777`) — the
  // one B2 verb that is read-only: it sends nothing and burns no attempt, so it
  // is safe on mount. It replaced an inference from block ①'s address_check,
  // which was a FALSE POSITIVE in exactly the window that occurred (B1 rolled,
  // B2 did not), because the other two B2 verbs cannot be probed: `start` SENDS
  // real mail from the org's mailbox and `confirm` would burn one of five
  // attempts.
  //
  // It doubles as REMOUNT RECOVERY: a reload mid-verification gets the pending
  // address + expiry back and keeps its countdown, instead of dropping to the
  // address form while a live code is still in flight.
  useEffect(() => {
    if (!rpc || connectionState !== "connected" || verifySupported !== undefined) return;
    let cancelled = false;
    rpc
      .mailOwnerVerifyStatus()
      .then((res) => {
        if (cancelled) return;
        setVerifySupported(true);
        if (res.pending) {
          setPendingVerify(res.pending);
          setNowMs(Date.now());
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setVerifySupported(!methodNotFound(err));
      });
    return () => {
      cancelled = true;
    };
  }, [rpc, connectionState, verifySupported]);

  /** Render each rejection honestly — collapsing the five into one "falhou"
   *  hides the only thing the user can act on. `attemptsRemaining` and
   *  `retryAfterSeconds` ride on the error `details`. */
  function verifyErrorMessage(err: unknown): string {
    const code = (err as { code?: string })?.code ?? "";
    const details = ((err as { details?: unknown })?.details ?? {}) as {
      attemptsRemaining?: number;
      retryAfterSeconds?: number;
    };
    // The runtime currently carries the COUNT in the message text rather than in
    // `details` (live probe 2026-07-27: code VERIFY_CODE_INCORRECT, message
    // "código incorreto — 3 tentativa(s) restante(s)", details undefined). My
    // localized string is preferred when the structured field exists, but when
    // it does not, the server's message is STRICTLY better than mine because it
    // is the only thing carrying the warn-before-lockout count — dropping to a
    // bare "Código incorreto." would discard the one fact the user can act on.
    const serverMsg = err instanceof Error ? err.message : "";
    const serverHasCount = /\d/.test(serverMsg);
    switch (code) {
      case "VERIFY_CODE_INCORRECT":
        if (typeof details.attemptsRemaining === "number") {
          return t("agent.emailSetup.ep2ErrIncorrectWithAttempts", { n: details.attemptsRemaining });
        }
        return serverHasCount ? serverMsg : t("agent.emailSetup.ep2ErrIncorrect");
      case "VERIFY_CODE_EXPIRED":
        return t("agent.emailSetup.ep2ErrExpired");
      case "VERIFY_NO_PENDING":
        return t("agent.emailSetup.ep2ErrNoPending");
      case "VERIFY_TOO_MANY_ATTEMPTS":
        return t("agent.emailSetup.ep2ErrBurned");
      case "VERIFY_RESEND_TOO_SOON":
        // Same shape: prefer the structured seconds, else the server's own text
        // if it names a number, else my generic 60s default.
        if (typeof details.retryAfterSeconds === "number") {
          return t("agent.emailSetup.ep2ErrResendTooSoon", { seconds: details.retryAfterSeconds });
        }
        return serverHasCount
          ? serverMsg
          : t("agent.emailSetup.ep2ErrResendTooSoon", { seconds: 60 });
      default:
        return err instanceof Error ? err.message : String(err);
    }
  }

  async function startVerify() {
    if (!rpc || verifyBusy) return;
    setVerifyBusy(true);
    setVerifyError(null);
    try {
      const res = await rpc.mailOwnerVerifyStart({ personalEmail: personalEmail.trim() });
      setPendingVerify(res);
      setCode("");
      setNowMs(Date.now());
      // Mirror the server's 60s cooldown so the resend button explains itself.
      setResendReadyAt(Date.now() + 60_000);
    } catch (err) {
      // Self-correction for the inferred support flag above: if the verb turns
      // out not to exist, fall back to the honest pending note instead of
      // leaving a form that cannot work.
      if (methodNotFound(err)) setVerifySupported(false);
      setVerifyError(verifyErrorMessage(err));
      const retry = ((err as { details?: { retryAfterSeconds?: number } })?.details ?? {})
        .retryAfterSeconds;
      if (typeof retry === "number") setResendReadyAt(Date.now() + retry * 1000);
    } finally {
      setVerifyBusy(false);
    }
  }

  async function confirmVerify() {
    if (!rpc || verifyBusy) return;
    setVerifyBusy(true);
    setVerifyError(null);
    try {
      await rpc.mailOwnerVerifyConfirm({ code });
      setPendingVerify(null);
      setCode("");
      // The stamp lands on the OWNER contact, so refresh identity rather than
      // trusting the response echo — same discipline as the rename flow.
      onChanged?.();
    } catch (err) {
      const c = (err as { code?: string })?.code;
      // On a wrong code, RE-READ status: the count lives on that success payload
      // (the error envelope drops extras), so this is the only way to get the
      // NUMBER rather than the runtime's pt-BR sentence — which matters because
      // this pane ships in three locales and the wording must be ours.
      if (c === "VERIFY_CODE_INCORRECT" && rpc) {
        try {
          const st = await rpc.mailOwnerVerifyStatus();
          if (st.pending) setPendingVerify(st.pending);
          if (typeof st.pending?.attemptsRemaining === "number") {
            setVerifyError(
              t("agent.emailSetup.ep2ErrIncorrectWithAttempts", { n: st.pending.attemptsRemaining }),
            );
            return;
          }
        } catch {
          // Status unreadable — fall through to the message-based path below,
          // which still carries a human count. A degraded count beats none.
        }
      }
      setVerifyError(verifyErrorMessage(err));
      // A burned or expired code cannot be retried — drop back to the address
      // step so the only offered action is the one that can actually succeed.
      if (c === "VERIFY_TOO_MANY_ATTEMPTS" || c === "VERIFY_NO_PENDING") setPendingVerify(null);
    } finally {
      setVerifyBusy(false);
    }
  }

  const fromPreview = assistant
    ? assistantEmailFromPreview(
        displayName.trim() || undefined,
        assistantDisplayName(assistant),
        owner?.displayName ?? "",
        address ?? undefined,
      )
    : null;

  const blockTitle =
    "flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-coffee-600 dark:text-cream-400";
  const badgeOk =
    "inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300";
  const badgePending =
    "inline-flex items-center rounded-full bg-cream-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-coffee-500 dark:bg-coffee-800 dark:text-cream-500";

  return (
    <div className="space-y-4" data-testid="email-setup-blocks">
      {/* ── ① A CAIXA DO ASSISTENTE ───────────────────────────────── */}
      <section data-testid="email-setup-block-1">
        <div className={blockTitle}>
          <span>① {t("agent.emailSetup.ep1Title")}</span>
          <span className={badgeOk}>
            <Check size={10} aria-hidden />
            {t("agent.emailSetup.ep1Active")}
          </span>
        </div>
        <code className="mt-1.5 block truncate rounded bg-cream-100 px-2 py-1 text-xs text-coffee-800 dark:bg-coffee-800 dark:text-cream-100">
          {address ?? "—"}
        </code>

        {/* The assistant's address is DEPLOYMENT-IMMUTABLE (operator ruling
            2026-07-27). The B1 recomposition affordance was removed: a rename
            is ADDITIVE at the provider — addresses are immutable and there is
            no alias primitive — so every "rename" permanently accumulates a
            live inbox that only a human console action can retire. The verbs
            (mail.address_check / mail.address_rename) still exist runtime-side
            and are simply no longer called from here. */}
        <p className="mt-1 text-[11px] text-coffee-500 dark:text-cream-500">
          {t("agent.emailSetup.ep1Immutable")}
        </p>
      </section>

      {/* ── ② COMO SEUS E-MAILS ASSINAM ("De:") ───────────────────── */}
      <section data-testid="email-setup-block-2">
        <div className={blockTitle}>② {t("agent.emailSetup.fromTitle")}</div>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t("agent.hero.card.emailFromPlaceholder")}
          data-testid="email-setup-from-input"
          className="mt-1.5 w-full rounded border border-cream-300 bg-transparent px-2 py-1 text-sm dark:border-coffee-700"
        />
        {fromPreview && (
          <p
            className="mt-1.5 rounded bg-cream-100 px-2 py-1 text-xs text-coffee-700 dark:bg-coffee-800/60 dark:text-cream-300"
            data-testid="email-setup-from-preview"
          >
            <span className="mr-1 font-semibold text-coffee-500 dark:text-cream-500">
              {t("agent.emailSetup.fromPreviewLabel")}
            </span>
            {fromPreview}
          </p>
        )}
        <p className="mt-1 text-[11px] text-coffee-500 dark:text-cream-500">
          {t("agent.emailSetup.fromSyncNote")}
        </p>
        {error && (
          <p className="mt-1.5 rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300">
            {error}
          </p>
        )}
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void saveFrom()}
            disabled={saving || !assistant}
            data-testid="email-setup-from-save"
            className="inline-flex items-center gap-1.5 rounded border border-gold-500/60 bg-gold-400/10 px-3 py-1 text-xs font-medium text-gold-700 transition-colors hover:bg-gold-400/25 disabled:opacity-50 dark:border-gold-500/40 dark:text-gold-400"
          >
            {saving && <Loader2 size={11} aria-hidden className="animate-spin" />}
            {t("common.save", "Salvar")}
          </button>
          {saved && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400">
              {t("common.saved", "Salvo ✓")}
            </span>
          )}
        </div>
      </section>

      {/* ── ③ SEU E-MAIL — para o assistente reconhecer VOCÊ ──────── */}
      <section data-testid="email-setup-block-3">
        <div className={blockTitle}>
          <span>③ {t("agent.emailSetup.ep2Title")}</span>
          {ownerVerified ? (
            <span className={badgeOk}>
              <Check size={10} aria-hidden />
              {t("agent.emailSetup.ep2Verified")}
            </span>
          ) : (
            <span className={badgePending}>{t("agent.emailSetup.ep2Pending")}</span>
          )}
        </div>
        <p className="mt-1 text-body-sm text-coffee-600 dark:text-cream-400">
          {t("agent.emailSetup.ep2Benefit")}
        </p>

        {ownerVerified ? (
          /* Terminal state. `verified` is stamped ONLY by the runtime
             (mail.owner_verify_confirm) — rendered, never fabricated. */
          <code
            data-testid="email-setup-ep2-verified-address"
            className="mt-1.5 block truncate rounded bg-cream-100 px-2 py-1 text-xs text-coffee-800 dark:bg-coffee-800 dark:text-cream-100"
          >
            {ownerVerified.address}
          </code>
        ) : verifySupported === false ? (
          /* The B2 verbs ship on the same roll as block ①'s — until then the
             honest §2.5 state, same method-not-found grammar as every channel
             surface. */
          <p
            className="mt-1.5 rounded bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300"
            data-testid="email-setup-ep2-pending"
          >
            {t("agent.emailSetup.ep2PendingNote")}
          </p>
        ) : !pendingVerify ? (
          <div className="mt-2 space-y-2">
            <input
              type="email"
              value={personalEmail}
              onChange={(e) => setPersonalEmail(e.target.value)}
              placeholder={t("agent.emailSetup.ep2EmailPlaceholder")}
              data-testid="email-setup-ep2-email"
              className="w-full rounded border border-cream-300 bg-transparent px-2 py-1.5 text-sm dark:border-coffee-700"
            />
            {verifyError && (
              <p
                data-testid="email-setup-ep2-error"
                className="rounded bg-red-500/10 p-1.5 text-[11px] text-red-600 dark:text-red-300"
              >
                {verifyError}
              </p>
            )}
            <button
              type="button"
              data-testid="email-setup-ep2-send"
              disabled={verifyBusy || !personalEmail.trim()}
              onClick={() => void startVerify()}
              className="inline-flex items-center gap-1.5 rounded border border-gold-500/60 bg-gold-400/10 px-3 py-1 text-[11px] font-medium text-gold-700 hover:bg-gold-400/25 disabled:opacity-50 dark:border-gold-500/40 dark:text-gold-400"
            >
              {verifyBusy && <Loader2 size={10} aria-hidden className="animate-spin" />}
              {t("agent.emailSetup.ep2Send")}
            </button>
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            {/* Always name WHERE the code went (operator preference) — a code
                with no stated destination is the classic setup dead end. */}
            <p className="text-[11px] text-coffee-600 dark:text-cream-400">
              {t("agent.emailSetup.ep2CodeSent", { address: pendingVerify.address })}
            </p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              data-testid="email-setup-ep2-code"
              className="w-full rounded border border-cream-300 bg-transparent px-2 py-1.5 text-center text-lg tracking-[0.4em] dark:border-coffee-700"
            />
            <p className="text-[11px] text-coffee-500 dark:text-cream-500">
              {secondsLeft > 0
                ? t("agent.emailSetup.ep2Expires", { mmss: formatMmSs(secondsLeft) })
                : t("agent.emailSetup.ep2Expired")}
              {/* Reload-durable: after a refresh mid-verification this is the
                  only place the user can learn how many tries are left. */}
              {typeof pendingVerify.attemptsRemaining === "number" && (
                <span data-testid="email-setup-ep2-attempts">
                  {" "}
                  {t("agent.emailSetup.ep2AttemptsLeft", { n: pendingVerify.attemptsRemaining })}
                </span>
              )}
            </p>

            {verifyError && (
              <p
                data-testid="email-setup-ep2-error"
                className="rounded bg-red-500/10 p-1.5 text-[11px] text-red-600 dark:text-red-300"
              >
                {verifyError}
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                data-testid="email-setup-ep2-resend"
                disabled={verifyBusy || resendIn > 0}
                onClick={() => void startVerify()}
                className="text-[11px] text-coffee-600 underline-offset-2 hover:underline disabled:opacity-50 dark:text-cream-400"
              >
                {resendIn > 0
                  ? t("agent.emailSetup.ep2ResendIn", { seconds: resendIn })
                  : t("agent.emailSetup.ep2Resend")}
              </button>
              <button
                type="button"
                data-testid="email-setup-ep2-confirm"
                disabled={verifyBusy || code.length !== 6}
                onClick={() => void confirmVerify()}
                className="inline-flex items-center gap-1.5 rounded border border-gold-500/60 bg-gold-400/10 px-3 py-1 text-[11px] font-medium text-gold-700 hover:bg-gold-400/25 disabled:opacity-50 dark:border-gold-500/40 dark:text-gold-400"
              >
                {verifyBusy && <Loader2 size={10} aria-hidden className="animate-spin" />}
                {t("agent.emailSetup.ep2Confirm")}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── the rules editor, rehosted (§3.8) ─────────────────────── */}
      <section className="border-t border-cream-200/70 pt-3 dark:border-coffee-800/60">
        <EmailRulesEditor />
      </section>
    </div>
  );
}
